import type { SiteConfig } from "../siteConfig";

const normalizeBaseUrl = (value?: string) => {
  const fallback = "";
  const trimmed = value?.trim() || fallback;
  return trimmed.replace(/\/+$/, "");
};

export const API_BASE_URL = normalizeBaseUrl(process.env.REACT_APP_API_URL);

export interface ApiHealthResponse {
  status: string;
  database?: string;
}

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  roles: string[];
  products: string[];
  document?: string;
  avatar?: string;
}

export interface AuthResponse {
  token: string;
  user: AuthUser;
}

const LOCAL_SESSION_PREFIX = "local-session:";

export interface ProductItem {
  id: string;
  title: string;
  description: string;
  badge: string;
  status: string;
  color: string;
  banner?: string;
  checkoutUrl: string | null;
  productUrl: string | null;
  isOffer: boolean;
  isCourse: boolean;
  isGlobal: boolean;
  requiresExternalSignup?: boolean;
  externalSignupUrl?: string | null;
  openMode?: "same" | "new_tab" | "modal";
  checkoutOpenMode?: "same" | "new_tab" | "modal";
  linkedOfferIds: string[];
  hiddenOfferIds?: string[];
  sortOrder: number;
  hasAccess: boolean;
  accessUrl: string | null;
}

export interface StatsResponse {
  totalStudents: number;
  activeStudents: number;
}

export interface OnlineUsersResponse {
  onlineUsers: number;
}

export interface HomeBanner {
  id: number;
  title: string;
  subtitle: string;
  image_url: string;
  mobile_image_url: string | null;
  cta_label: string;
  cta_url: string | null;
  is_active: boolean;
  sort_order: number;
}

export interface UserStatsResponse {
  completionPercent: number;
  watchedLessons: number;
  totalLessons: number;
  toolsPercent: number;
  ownedProducts: number;
  totalProducts: number;
}

export interface PlanilhaAccessResponse {
  hasAccess: boolean;
}

export interface PlanilhaSettings {
  dailyTargetProfit: number;
  dailyMaxLoss: number;
  dailyMaxOperations: number;
  weeklyTargetProfit: number;
  weeklyMaxLoss: number;
  weeklyMaxOperations: number;
  monthlyTargetProfit: number;
  monthlyMaxLoss: number;
  monthlyMaxOperations: number;
  defaultEntryAmount: number;
  enableMartingale: boolean;
  martingaleMultiplier: number;
  maxMartingaleLevel: number;
}

export interface PlanilhaOperation {
  id: number;
  type: "CALL" | "PUT";
  amount: number;
  result: number;
  createdAt: string;
}

export interface PlanilhaDailyGoal {
  targetProfit: number;
  maxLoss: number;
  currentProfit: number;
  currentLoss: number;
  operationsCount: number;
  maxOperations: number;
}

export interface CourseMaterial {
  title: string;
  url: string;
}

export interface LessonItem {
  id: number;
  module_id: number;
  title: string;
  description: string;
  video_url: string;
  thumbnail_url: string;
  materials: CourseMaterial[];
  sort_order: number;
  watched?: boolean;
}

export interface CourseModule {
  id: number;
  course_product_id: number;
  title: string;
  cover_url: string;
  sort_order: number;
  lessons: LessonItem[];
}

interface CourseModulesResponse {
  modules: CourseModule[];
  watchedLessonIds?: number[];
}

export interface AdminAuthUser {
  id: number;
  email: string;
  role: string;
}

export interface AdminAuthResponse {
  token: string;
  admin: AdminAuthUser;
}

export interface AdminUser {
  id: number;
  document: string;
  name: string | null;
  email: string | null;
  customer_email: string | null;
  phone_number: string | null;
  customer_phone_number: string | null;
  cpf: string | null;
  customer_cpf: string | null;
  status: string | null;
  is_customer: boolean;
  webhook_created_at: string | null;
  first_purchase_at: string | null;
  total_orders: number;
  total_revenue: number;
  offer_ids: string[];
  external_signup_completed: string[];
  utm_src: string | null;
}

export interface AdminUsersQuery {
  kind?: "all" | "leads" | "customers";
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface AdminUsersResponse {
  users: AdminUser[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface AdminProduct {
  id: number;
  title: string;
  description: string;
  banner_url: string;
  is_course: boolean;
  is_mentoria: boolean;
  is_offer: boolean;
  offer_id: string;
  checkout_url: string | null;
  product_url: string;
  requires_external_signup: boolean;
  external_signup_url: string | null;
  open_mode: "same" | "new_tab" | "modal";
  checkout_open_mode: "same" | "new_tab" | "modal";
  is_global: boolean;
  linked_offer_ids: string[];
  additional_offer_ids: string[];
  hide_offer_ids: string[];
  is_active: boolean;
  sort_order: number;
}

export interface AdminProductInput {
  title: string;
  description: string;
  banner_url: string;
  is_course: boolean;
  is_mentoria: boolean;
  is_offer: boolean;
  offer_id?: string;
  checkout_url?: string | null;
  product_url: string;
  requires_external_signup?: boolean;
  external_signup_url?: string | null;
  open_mode?: "same" | "new_tab" | "modal";
  checkout_open_mode?: "same" | "new_tab" | "modal";
  is_global?: boolean;
  linked_offer_ids?: string[];
  additional_offer_ids?: string[];
  hide_offer_ids?: string[];
  is_active?: boolean;
  sort_order?: number;
}

export interface AdminHomeBanner extends HomeBanner {}

export interface AdminHomeBannerInput {
  title: string;
  subtitle?: string | null;
  image_url: string;
  mobile_image_url?: string | null;
  cta_label?: string | null;
  cta_url?: string | null;
  is_active?: boolean;
  sort_order?: number;
}

export interface AdminCourseModule {
  id: number;
  course_product_id: number;
  title: string;
  cover_url: string;
  sort_order: number;
  lessons: AdminCourseLesson[];
}

export interface AdminCourseLesson {
  id: number;
  module_id: number;
  title: string;
  description: string;
  video_url: string;
  thumbnail_url: string;
  materials: CourseMaterial[];
  sort_order: number;
}

export interface AdminCourseModuleInput {
  course_product_id: number;
  title: string;
  cover_url?: string | null;
  sort_order?: number;
}

export interface AdminCourseLessonInput {
  title: string;
  description?: string | null;
  video_url: string;
  thumbnail_url?: string | null;
  materials?: CourseMaterial[];
  sort_order?: number;
}

export interface AdminCertificate {
  id: number;
  userId: string | null;
  userName: string;
  userEmail: string | null;
  programName: string;
  progressPercent: number;
  certificateCode: string;
  issuedAt: string;
}

function parseStringArray(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.map((value) => String(value).trim()).filter(Boolean);
  }
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw) as unknown;
      return parseStringArray(parsed);
    } catch {
      return [];
    }
  }
  return [] as string[];
}

async function request<T>(path: string, init?: RequestInit) {
  let response: Response;

  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers: {
        Accept: "application/json",
        ...(init?.headers || {}),
      },
    });
  } catch (error) {
    if (
      error instanceof TypeError ||
      (error instanceof Error &&
        /failed to fetch|networkerror|load failed|fetch|connection refused/i.test(
          error.message,
        ))
    ) {
      throw new Error(
        `Nao foi possivel conectar com o backend${API_BASE_URL ? ` em ${API_BASE_URL}` : ""}. Verifique se a API esta online.`,
      );
    }

    throw error;
  }

  const text = await response.text();
  let data: T | { message?: string } | null = null;

  if (text) {
    try {
      data = JSON.parse(text) as T | { message?: string };
    } catch (error) {
      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("text/html") || text.trim().startsWith("<")) {
        throw new Error(
          `A API retornou HTML em ${path}. No ambiente local, confira se o proxy/backend esta apontando para a API correta.`,
        );
      }
      throw error;
    }
  }

  if (!response.ok) {
    const message =
      typeof data === "object" && data && "message" in data && typeof data.message === "string"
        ? data.message
        : `API respondeu com status ${response.status}`;
    throw new Error(message);
  }

  return data as T;
}

async function adminRequest<T>(path: string, token: string, init?: RequestInit) {
  return request<T>(path, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init?.headers || {}),
    },
  });
}

/**
 * GET /api/site-config — configurações do site editadas no painel ADMIN
 * (links sociais, corretora, popups de boas-vindas/liberar acesso, agenda da
 * live e avisos do sininho). Contrato = shape de `SiteConfig` (src/siteConfig.ts).
 *
 * O backend pode devolver o objeto direto ou `{ config: {...} }`, e pode omitir
 * campos — o App faz merge sobre os defaults via `mergeSiteConfig()`.
 *
 * Enquanto a rota não existir no backend, esta função rejeita e o App mantém
 * `defaultSiteConfig`. Não precisa mexer no front para conectar: basta o backend
 * responder este endpoint.
 */
export async function getSiteConfig(token?: string): Promise<Partial<SiteConfig>> {
  const data = token
    ? await adminRequest<Partial<SiteConfig> | { config?: Partial<SiteConfig> }>(
        "/api/site-config",
        token,
      )
    : await request<Partial<SiteConfig> | { config?: Partial<SiteConfig> }>(
        "/api/site-config",
      );
  const payload = (data || {}) as { config?: Partial<SiteConfig> } & Partial<SiteConfig>;
  return (payload.config ?? payload) as Partial<SiteConfig>;
}

export function updateSiteConfig(token: string, payload: SiteConfig) {
  return adminRequest<{ ok: boolean; config: SiteConfig }>("/api/site-config", token, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function issueCertificate(token: string, payload: { programName: string; progressPercent: number }) {
  return request<{ ok: boolean; certificateCode: string }>("/api/certificates/issue", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function getAdminCertificates(token: string) {
  return adminRequest<{ certificates: AdminCertificate[] }>("/api/certificates/admin", token, {
    method: "GET",
  });
}

function shouldUseLocalAuthFallback(error: unknown) {
  return (
    error instanceof TypeError ||
    (error instanceof Error &&
      /failed to fetch|networkerror|load failed|fetch/i.test(error.message))
  );
}

function createLocalAuthResponse(mode: "demo" | "guest"): AuthResponse {
  if (mode === "guest") {
    return {
      token: `${LOCAL_SESSION_PREFIX}guest`,
      user: {
        id: "local-guest",
        name: "Visitante",
        email: "",
        roles: ["guest"],
        products: [],
        document: "",
      },
    };
  }

  return {
    token: `${LOCAL_SESSION_PREFIX}demo`,
    user: {
      id: "local-demo",
      name: "Demo Local",
      email: "demo@local",
      roles: ["member"],
      products: ["local-access"],
      document: "DEMO",
    },
  };
}

export function isLocalSessionToken(token: string) {
  return token.startsWith(LOCAL_SESSION_PREFIX);
}

export function getApiHealth(signal?: AbortSignal) {
  return request<ApiHealthResponse>("/api/health", {
    method: "GET",
    signal,
  });
}

export function loginDemo() {
  return request<AuthResponse>("/api/auth/login", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email: "membro@bulls.ai",
      password: "membros123",
    }),
  }).catch((error) => {
    if (shouldUseLocalAuthFallback(error)) {
      return createLocalAuthResponse("demo");
    }
    throw error;
  });
}

export function loginGuest() {
  return request<AuthResponse>("/api/auth/login-guest", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({}),
  }).catch((error) => {
    if (shouldUseLocalAuthFallback(error)) {
      return createLocalAuthResponse("guest");
    }
    throw error;
  });
}

export function loginByDocument(document: string) {
  return request<AuthResponse>("/api/auth/login-document", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ document }),
  });
}

export function getMe(token: string) {
  return request<{ user: AuthUser }>("/api/auth/me", {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export function getProducts(token: string) {
  return request<{ products: ProductItem[] }>("/api/products", {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export function getPublicStats() {
  return request<StatsResponse>("/api/stats", {
    method: "GET",
  });
}

export function getOnlineUsers() {
  return request<OnlineUsersResponse>("/api/stats/online-users", {
    method: "GET",
  });
}

export function getHomeBanners() {
  return request<{ banners: HomeBanner[] }>("/api/home-banners", {
    method: "GET",
  });
}

export function getUserStats(token: string) {
  return request<UserStatsResponse>("/api/stats/user-stats", {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export function getCourseModules(courseKey: string, token: string) {
  return request<CourseModulesResponse>(`/api/courses/${courseKey}/modules`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export function markLessonWatched(lessonId: number, token: string) {
  return request<{ ok: boolean }>(`/api/courses/lessons/${lessonId}/watched`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export function getPlanilhaAccess(token: string) {
  return request<PlanilhaAccessResponse>("/api/planilha/access-check", {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export function getPlanilhaSettings(token: string) {
  return request<{ settings: PlanilhaSettings }>("/api/planilha/settings", {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export function getPlanilhaOperations(token: string) {
  return request<{ operations: PlanilhaOperation[] }>("/api/planilha/operations", {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export function getPlanilhaDailyGoal(token: string) {
  return request<{ dailyGoal: PlanilhaDailyGoal }>("/api/planilha/goals/daily", {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export function createPlanilhaOperation(
  token: string,
  payload: { type: "CALL" | "PUT"; amount: number; result: number }
) {
  return request<{ message: string; id: number }>("/api/planilha/operations", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function updatePlanilhaSettings(token: string, payload: PlanilhaSettings) {
  return request<{ message: string }>("/api/planilha/settings", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function adminLogin(email: string, password: string) {
  return request<AdminAuthResponse>("/api/admin/auth/login", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email, password }),
  });
}

export async function getAdminUsers(token: string, query?: AdminUsersQuery) {
  const searchParams = new URLSearchParams();
  if (query?.kind && query.kind !== "all") searchParams.set("kind", query.kind);
  if (query?.search) searchParams.set("search", query.search);
  if (typeof query?.page === "number") searchParams.set("page", String(query.page));
  if (typeof query?.pageSize === "number") searchParams.set("pageSize", String(query.pageSize));

  const response = await adminRequest<{
    users: Array<Record<string, unknown>>;
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  }>(
    `/api/admin/users${searchParams.toString() ? `?${searchParams.toString()}` : ""}`,
    token,
    {
      method: "GET",
    },
  );

  return {
    users: response.users.map((user) => ({
      id: Number(user.id),
      document: String(user.document ?? ""),
      name: typeof user.name === "string" ? user.name : null,
      email: typeof user.email === "string" ? user.email : null,
      customer_email: typeof user.customer_email === "string" ? user.customer_email : null,
      phone_number: typeof user.phone_number === "string" ? user.phone_number : null,
      customer_phone_number:
        typeof user.customer_phone_number === "string" ? user.customer_phone_number : null,
      cpf: typeof user.cpf === "string" ? user.cpf : null,
      customer_cpf: typeof user.customer_cpf === "string" ? user.customer_cpf : null,
      status: typeof user.status === "string" ? user.status : null,
      is_customer: Boolean(user.is_customer),
      webhook_created_at:
        typeof user.webhook_created_at === "string"
          ? user.webhook_created_at
          : user.webhook_created_at instanceof Date
            ? user.webhook_created_at.toISOString()
            : null,
      first_purchase_at:
        typeof user.first_purchase_at === "string"
          ? user.first_purchase_at
          : user.first_purchase_at instanceof Date
            ? user.first_purchase_at.toISOString()
            : null,
      total_orders: Number(user.total_orders ?? 0),
      total_revenue: Number(user.total_revenue ?? 0),
      offer_ids: parseStringArray(user.offer_ids),
      external_signup_completed: parseStringArray(user.external_signup_completed),
      utm_src: typeof user.utm_src === "string" ? user.utm_src : null,
    })) satisfies AdminUser[],
    total: Number(response.total ?? 0),
    page: Number(response.page ?? 1),
    pageSize: Number(response.pageSize ?? response.users.length ?? 20),
    totalPages: Number(response.totalPages ?? 1),
  } satisfies AdminUsersResponse;
}

export function updateAdminUser(
  token: string,
  document: string,
  payload: Partial<{
    name: string;
    email: string;
    phone_number: string;
    customer_email: string;
    customer_phone_number: string;
    cpf: string;
    customer_cpf: string;
    status: string;
    is_customer: boolean;
    first_purchase_at: string;
    total_orders: number;
    total_revenue: number;
    offer_ids: string[];
    external_signup_completed: string[];
    utm_src: string;
    webhook_created_at: string;
  }>,
) {
  return adminRequest<{ ok: boolean }>(`/api/admin/users/${document}`, token, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function deleteAdminUser(token: string, document: string) {
  return adminRequest<{ ok: boolean }>(`/api/admin/users/${document}`, token, {
    method: "DELETE",
  });
}

export function getAdminProducts(token: string) {
  return adminRequest<{ products: AdminProduct[] }>("/api/admin/products", token, {
    method: "GET",
  });
}

export function createAdminProduct(token: string, payload: AdminProductInput) {
  return adminRequest<{ ok: boolean }>("/api/admin/products", token, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function updateAdminProduct(
  token: string,
  id: number,
  payload: Partial<AdminProductInput>,
) {
  return adminRequest<{ ok: boolean }>(`/api/admin/products/${id}`, token, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function deleteAdminProduct(token: string, id: number) {
  return adminRequest<{ ok: boolean }>(`/api/admin/products/${id}`, token, {
    method: "DELETE",
  });
}

export function getAdminHomeBanners(token: string) {
  return adminRequest<{ banners: AdminHomeBanner[] }>("/api/admin/home-banners", token, {
    method: "GET",
  });
}

export function createAdminHomeBanner(token: string, payload: AdminHomeBannerInput) {
  return adminRequest<{ ok: boolean }>("/api/admin/home-banners", token, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function updateAdminHomeBanner(
  token: string,
  id: number,
  payload: Partial<AdminHomeBannerInput>,
) {
  return adminRequest<{ ok: boolean }>(`/api/admin/home-banners/${id}`, token, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function deleteAdminHomeBanner(token: string, id: number) {
  return adminRequest<{ ok: boolean }>(`/api/admin/home-banners/${id}`, token, {
    method: "DELETE",
  });
}

export function getAdminCourseModules(token: string, courseId?: number) {
  const query = courseId ? `?courseId=${courseId}` : "";
  return adminRequest<{ modules: AdminCourseModule[] }>(
    `/api/admin/course-modules${query}`,
    token,
    {
      method: "GET",
    },
  );
}

export function createAdminCourseModule(
  token: string,
  payload: AdminCourseModuleInput,
) {
  return adminRequest<{ ok: boolean }>("/api/admin/course-modules", token, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function updateAdminCourseModule(
  token: string,
  id: number,
  payload: Partial<AdminCourseModuleInput>,
) {
  return adminRequest<{ ok: boolean }>(`/api/admin/course-modules/${id}`, token, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function deleteAdminCourseModule(token: string, id: number) {
  return adminRequest<{ ok: boolean }>(`/api/admin/course-modules/${id}`, token, {
    method: "DELETE",
  });
}

export function createAdminCourseLesson(
  token: string,
  moduleId: number,
  payload: AdminCourseLessonInput,
) {
  return adminRequest<{ ok: boolean }>(`/api/admin/course-modules/${moduleId}/lessons`, token, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function updateAdminCourseLesson(
  token: string,
  id: number,
  payload: Partial<AdminCourseLessonInput>,
) {
  return adminRequest<{ ok: boolean }>(`/api/admin/course-lessons/${id}`, token, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function deleteAdminCourseLesson(token: string, id: number) {
  return adminRequest<{ ok: boolean }>(`/api/admin/course-lessons/${id}`, token, {
    method: "DELETE",
  });
}
